﻿package org.example.project.screens_cus_mobile

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.example.project.dashboard.decodedFocusHtml
import org.example.project.enginekalender.kalenderTodayIsoDate
import org.example.project.enginekalender.loadOwnKalenderAufgaben
import org.example.project.enginekalender.saveOwnKalenderAufgabe
import org.example.project.screens_shared.taskParagraphHtml
import org.example.project.service.GlobalManager
import org.example.project.utils.All
import org.example.project.utils.AufgabeZeile
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Iframe
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLIFrameElement
import org.w3c.dom.events.Event
import kotlin.js.Date

private object AufgabenViewerStyles : StyleSheet() {
    val viewerRoot by style {
        width(100.percent)
        height(100.percent)
        minHeight(0.px)
        position(Position.Relative)
        padding(0.px)
        boxSizing("border-box")
        color(Color.white)
        overflow("hidden")
    }

    val immersiveViewer by style {
        position(Position.Fixed)
        top(0.px)
        left(0.px)
        width(100.vw)
        height(100.vh)
        property("z-index", "9999")
        property("background", "#15181d")
    }

    val tabletBody by style {
        width(100.percent)
        height(100.percent)
        minHeight(0.px)
        display(DisplayStyle.Grid)
        property("grid-template-columns", "minmax(0, 1fr) minmax(0, 3fr) minmax(0, 1fr)")
        gap(12.px)
        media(mediaMaxWidth(760.px)) { self style { display(DisplayStyle.None) } }
    }

    val phoneBody by style {
        display(DisplayStyle.None)
        media(mediaMaxWidth(760.px)) {
            self style {
                display(DisplayStyle.Grid)
                property("grid-template-rows", "minmax(0, 1fr) minmax(0, 3fr) minmax(0, 1fr)")
                height(100.percent)
                minHeight(0.px)
                overflow("hidden")
            }
        }
    }

    val phoneSchedule by style {
        display(DisplayStyle.None)
        media(mediaMaxWidth(760.px)) {
            self style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Row)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                minHeight(0.px)
                overflowY("auto")
                padding(0.px, 14.px)
                boxSizing("border-box")
            }
        }
    }

    val schedule by style {
        minHeight(0.px)
        overflow("hidden")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Row)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        padding(6.px)
    }

    val focusPanel by style {
        position(Position.Relative)
        minWidth(0.px)
        minHeight(0.px)
        overflow("hidden")
        property("background", "transparent")
        padding(0.px)
        width(100.percent)
        height(100.percent)
        boxSizing("border-box")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        media(mediaMaxWidth(760.px)) {
            self style {
                property("height", "100%")
                padding(0.px)
            }
        }
    }

    val taskPopup by style {
        position(Position.Fixed)
        property("inset", "0")
        property("z-index", "10000")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        property("background", "rgba(0,0,0,.72)")
        padding(20.px)
        boxSizing("border-box")
    }

    val taskPopupCard by style {
        width(100.percent)
        maxWidth(720.px)
        property("max-height", "85vh")
        overflowY("auto")
        property("background", "#171b22")
        property("border", "1px solid rgba(255,255,255,.18)")
        property("border-radius", "12px")
        padding(16.px)
        boxSizing("border-box")
    }

    val loadError by style {
        position(Position.Absolute)
        top(8.px)
        left(0.px)
        width(100.percent)
        textAlign("center")
        color(Color("#ff9b9b"))
    }
}

private fun viewerMinute(value: String): Int? {
    val parts = value.split(":")
    val hour = parts.getOrNull(0)?.toIntOrNull() ?: return null
    val minute = parts.getOrNull(1)?.toIntOrNull() ?: return null
    return if (hour in 0..23 && minute in 0..59) hour * 60 + minute else null
}

private fun viewerTaskStart(task: All): Double? {
    if (viewerMinute(task.uhrzeitVon) == null) return null
    return Date("${task.datum}T${task.uhrzeitVon}:00").getTime().takeUnless { it.isNaN() }
}

private fun viewerTaskEnd(task: All): Double? {
    if (viewerMinute(task.uhrzeitBis) == null) return null
    return Date("${task.datum}T${task.uhrzeitBis}:00").getTime().takeUnless { it.isNaN() }
}

private fun viewerTimelineTime(task: All, today: String): String =
    if (task.datum == today) task.uhrzeitVon else "${task.datum.takeLast(5)} ${task.uhrzeitVon}"

private fun viewerNextTaskLabel(task: All?): String? {
    val start = task?.let(::viewerTaskStart) ?: return null
    val date = Date(start)
    val weekday = listOf("Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag")[date.getDay()]
    val day = date.getDate().toString().padStart(2, '0')
    val month = (date.getMonth() + 1).toString().padStart(2, '0')
    val hour = date.getHours().toString().padStart(2, '0')
    val minute = date.getMinutes().toString().padStart(2, '0')
    return "$weekday, $day.$month.${date.getFullYear()} $hour:$minute Uhr"
}

@Composable
fun AufgabenViewerScreen() {
    Style(AufgabenViewerStyles)
    val scope = rememberCoroutineScope()
    var clockTick by remember { mutableStateOf(js("Date.now()") as Double) }
    var tasks by remember { mutableStateOf<List<All>>(emptyList()) }
    var loadError by remember { mutableStateOf("") }
    var showAllTasks by remember { mutableStateOf(false) }
    var selectedDayTaskId by remember { mutableStateOf("") }
    var savingLine by remember { mutableStateOf(false) }
    var lineError by remember { mutableStateOf("") }
    var immersive by remember { mutableStateOf(false) }
    val liveVersion = GlobalManager.jsonDomainVersion("aufgaben")

    DisposableEffect(Unit) {
        val handler: (Event) -> Unit = { event ->
            if (event.asDynamic().data?.type == "lumatica-task-ring-click") {
                selectedDayTaskId = ""
                showAllTasks = true
            }
        }
        window.addEventListener("message", handler)
        onDispose { window.removeEventListener("message", handler) }
    }

    LaunchedEffect(Unit) {
        delay(2_300)
        immersive = true
    }
    LaunchedEffect(Unit) {
        while (true) {
            clockTick = js("Date.now()") as Double
            delay(1_000)
        }
    }
    LaunchedEffect(liveVersion) {
        while (true) {
            runCatching { loadOwnKalenderAufgaben() }
                .onSuccess { tasks = it; loadError = "" }
                .onFailure { loadError = "Aufgaben konnten nicht geladen werden." }
            delay(30_000)
        }
    }

    val dateIso = kalenderTodayIsoDate()
    val todayTasks = tasks.filter { it.datum == dateIso }
        .sortedWith(compareBy<All> { it.uhrzeitVon }.thenBy { it.bezeichnung })
    val activeTask = todayTasks.firstOrNull { task ->
        val start = viewerTaskStart(task) ?: return@firstOrNull false
        val end = viewerTaskEnd(task) ?: return@firstOrNull false
        start <= clockTick && clockTick < end
    }
    val previousTask = tasks.filter { (viewerTaskEnd(it) ?: Double.POSITIVE_INFINITY) <= clockTick }
        .maxByOrNull { viewerTaskEnd(it) ?: Double.NEGATIVE_INFINITY }
    val nextTask = tasks.filter { (viewerTaskStart(it) ?: Double.NEGATIVE_INFINITY) > clockTick }
        .minByOrNull { viewerTaskStart(it) ?: Double.POSITIVE_INFINITY }
    val timelineTasks = todayTasks
    val selectedTask = timelineTasks.firstOrNull { it.id == selectedDayTaskId }

    // Reload the schedule once when a new task becomes active so the adjacent
    // previous/next entries are recalculated from the latest server data.
    LaunchedEffect(activeTask?.id) {
        if (activeTask != null) {
            runCatching { loadOwnKalenderAufgaben() }
                .onSuccess { tasks = it; loadError = "" }
                .onFailure { loadError = "Aufgaben konnten nicht geladen werden." }
        }
    }

    fun updateLine(task: All, line: AufgabeZeile, ist: Int, erledigt: Boolean) {
        val now = js("Date.now()") as Double
        if (savingLine || ist < 0 || task.id != activeTask?.id ||
            (viewerTaskStart(task) ?: Double.POSITIVE_INFINITY) > now ||
            (viewerTaskEnd(task) ?: Double.NEGATIVE_INFINITY) <= now) return
        scope.launch {
            savingLine = true
            lineError = ""
            try {
                saveOwnKalenderAufgabe(task.copy(aufgabeZeilen = task.aufgabeZeilen.map {
                    if (it.id == line.id) it.copy(ist = ist, erledigt = erledigt) else it
                }))
                tasks = loadOwnKalenderAufgaben()
            } catch (cause: Throwable) {
                lineError = cause.message ?: "Fortschritt konnte nicht gespeichert werden."
            } finally {
                savingLine = false
            }
        }
    }
    Div({ classes(AufgabenViewerStyles.viewerRoot); if (immersive) classes(AufgabenViewerStyles.immersiveViewer) }) {
        if (loadError.isNotBlank()) Span({ classes(AufgabenViewerStyles.loadError) }) { Text(loadError) }

        Div({ classes(AufgabenViewerStyles.tabletBody) }) {
            Div({ classes(AufgabenViewerStyles.schedule) }) {
                key(previousTask?.id, previousTask?.datum, previousTask?.uhrzeitVon, previousTask?.bezeichnung) {
                    ViewerScheduleRow(previousTask?.uhrzeitVon.orEmpty(), previousTask, false)
                }
            }
            key(activeTask?.id) { ViewerFocusContent(activeTask, nextTask) }
            Div({ classes(AufgabenViewerStyles.schedule) }) {
                key(nextTask?.id, nextTask?.datum, nextTask?.uhrzeitVon, nextTask?.bezeichnung) {
                    ViewerScheduleRow(nextTask?.uhrzeitVon.orEmpty(), nextTask, false)
                }
            }
        }

        Div({ classes(AufgabenViewerStyles.phoneBody) }) {
            Div({ classes(AufgabenViewerStyles.phoneSchedule) }) {
                key(previousTask?.id, previousTask?.datum, previousTask?.uhrzeitVon, previousTask?.bezeichnung) {
                    ViewerScheduleRow(previousTask?.uhrzeitVon.orEmpty(), previousTask, false)
                }
            }
            key(activeTask?.id) { ViewerFocusContent(activeTask, nextTask) }
            Div({ classes(AufgabenViewerStyles.phoneSchedule) }) {
                key(nextTask?.id, nextTask?.datum, nextTask?.uhrzeitVon, nextTask?.bezeichnung) {
                    ViewerScheduleRow(nextTask?.uhrzeitVon.orEmpty(), nextTask, false)
                }
            }
        }

        if (showAllTasks) {
            Div({
                classes(AufgabenViewerStyles.taskPopup)
                onClick { showAllTasks = false; selectedDayTaskId = "" }
            }) {
                Div({
                    classes(AufgabenViewerStyles.taskPopupCard)
                    onClick { it.stopPropagation() }
                }) {
                    Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(14.px) } }) {
                        selectedTask?.let { task ->
                            val editable = task.id == activeTask?.id
                            Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.SpaceBetween); gap(12.px) } }) {
                                Span({ style { color(Color.white); fontSize(17.px); fontWeight("800") } }) {
                                    Text("${task.uhrzeitVon}–${task.uhrzeitBis}  ${task.bezeichnung}")
                                }
                                Button({
                                    attr("aria-label", "Zur Timeline")
                                    onClick { selectedDayTaskId = "" }
                                    style { color(Color("#c4ced9")); property("background", "transparent"); border(0.px); fontSize(22.px); cursor("pointer") }
                                }) { Text("×") }
                            }
                            if (task.aufgabeZeilen.isNotEmpty()) task.aufgabeZeilen.forEach { line ->
                                ViewerTaskLineEditor(line, editable, savingLine) { ist, erledigt ->
                                    updateLine(task, line, ist, erledigt)
                                }
                            } else ViewerScheduleRow(task.uhrzeitVon, task, editable)
                        }
                        if (selectedTask == null) {
                            Span({ style {
                                alignSelf(AlignSelf.Center)
                                color(Color("#c4ced9"))
                                fontSize(13.px)
                                fontWeight("700")
                                textAlign("center")
                                width(100.percent)
                            } }) { Text("Tagesübersicht") }
                            timelineTasks.forEach { task ->
                                Div({
                                    onClick { selectedDayTaskId = task.id }
                                    style { cursor("pointer") }
                                }) { ViewerScheduleRow(viewerTimelineTime(task, dateIso), task, task.id == activeTask?.id, fullWidth = true, highlightTime = true) }
                            }
                            if (timelineTasks.isEmpty()) Span { Text("Keine Aufgaben") }
                        }
                        if (lineError.isNotBlank()) Span({ style { color(Color("#ff9b9b")) } }) { Text(lineError) }
                    }
                }
            }
        }
    }
}

@Composable
private fun ViewerScheduleRow(
    timeLabel: String,
    task: All?,
    running: Boolean,
    fullWidth: Boolean = false,
    highlightTime: Boolean = false
) {
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "minmax(42px, 20%) minmax(0, 80%)")
            if (fullWidth) {
                width(100.percent)
            } else {
                property("width", "min(100%, 320px)")
                property("max-width", "100%")
            }
            alignItems(AlignItems.Stretch)
            minWidth(0.px)
            boxSizing("border-box")
        }
    }) {
        Div({ style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            alignSelf(AlignSelf.Stretch)
            property("border-right", if (highlightTime && running) "1px solid #ff747b" else "1px solid rgba(255,255,255,.18)")
            minHeight(42.px)
            padding(6.px, 8.px)
        } }) {
            Span({ style { color(if (highlightTime && running) Color("#ff747b") else Color.white); fontSize(14.px); fontWeight("800"); property("font-variant-numeric", "tabular-nums") } }) { Text(timeLabel) }
        }
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                minWidth(0.px)
                padding(7.px, 10.px)
                property("text-align", "center")
            }
        }) {
            val html = task?.let { viewerTaskHtml(it, showCounts = fullWidth) }.orEmpty()
            if (task == null) Span({ style { color(Color("#c4ced9")); fontSize(13.px) } }) { Text("Keine Aufgaben") }
            else if (html.isNotBlank()) ViewerTaskHtml(task.id, html, running)
        }
    }
}

private fun viewerTaskContentHtml(raw: String): String {
    if (raw.isBlank()) return ""
    val host = document.createElement("div") as HTMLElement
    host.innerHTML = raw
    val tokens = host.querySelectorAll("[data-note-token]")
    for (index in 0 until tokens.length) {
        val token = tokens.item(index) as? HTMLElement ?: continue
        if (token.getAttribute("data-note-token") == "step") {
            token.replaceWith(document.createTextNode("- "))
        } else {
            token.remove()
        }
    }
    return taskParagraphHtml(host.innerHTML)
}

private fun viewerEscapeHtml(value: String): String = value
    .replace("&", "&amp;").replace("<", "&lt;").replace(">", "&gt;")

private fun viewerTaskHtml(task: All, showCounts: Boolean = false): String {
    val header = task.bezeichnung.trim().takeIf { it.isNotBlank() }?.let { label ->
        "<div class=\"task-header\" style=\"color:#fff;font-size:1.45em;font-weight:800;line-height:1.2;white-space:nowrap\">${viewerEscapeHtml(label)}</div>"
    }.orEmpty()
    if (task.aufgabeZeilen.isNotEmpty()) return header + "<div class=\"task-lines\">" + task.aufgabeZeilen.joinToString("") { line ->
        val count = if (showCounts) line.soll?.let { "<span class=\"task-value\" style=\"white-space:nowrap;margin-left:8px\">${line.ist} / $it</span>" }.orEmpty() else ""
        val check = if (line.erledigt) "<span class=\"task-check\" style=\"color:#9ad7a7;margin-left:10px\">✓</span>" else ""
        "<div class=\"task-line\"><span class=\"task-label\" style=\"white-space:nowrap\">${viewerEscapeHtml(line.label)}</span>$count$check</div>"
    } + "</div>"
    val content = viewerTaskContentHtml(task.beschreibung.orEmpty()).ifBlank { viewerEscapeHtml(task.bezeichnung) }
    return if (header.isBlank()) content else header + content.takeIf { task.beschreibung.orEmpty().isNotBlank() }.orEmpty()
}

@Composable
private fun ViewerTaskLineEditor(
    line: AufgabeZeile,
    editable: Boolean,
    saving: Boolean,
    onSave: (Int, Boolean) -> Unit
) {
    var draft by remember(line.id, line.ist) { mutableStateOf(line.ist.toString()) }
    var editing by remember(line.id) { mutableStateOf(false) }
    var confirming by remember(line.id) { mutableStateOf(false) }
    fun commit(done: Boolean, value: Int? = draft.toIntOrNull()) {
        if (value == null) return
        if (value < 0) return
        onSave(value, done)
        editing = false
        confirming = false
    }
    Div({ style {
        display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px)
        padding(10.px, 2.px); property("border-bottom", "1px solid rgba(255,255,255,.12)")
        property("flex-wrap", "wrap")
    } }) {
        Span({ style { flexGrow(1); minWidth(0.px); color(Color.white); fontSize(15.px); fontWeight("700") } }) { Text(line.label) }
        line.soll?.let { soll ->
            if (editing && editable) {
                Input(InputType.Text, attrs = {
                    value(draft)
                    attr("inputmode", "numeric")
                    attr("pattern", "[0-9]*")
                    attr("aria-label", "Ist-Anzahl für ${line.label}")
                    style { width(56.px); padding(4.px); color(Color.white); property("background", "#303a4c"); property("border", "1px solid rgba(255,255,255,.3)"); borderRadius(4.px); fontSize(15.px) }
                    onInput { draft = it.value?.toString().orEmpty() }
                    onKeyDown { event ->
                        if (event.key == "Tab" || event.key == "Enter") commit(line.erledigt)
                        if (event.key == "Escape") { draft = line.ist.toString(); editing = false; confirming = false }
                    }
                })
                Span({ style { color(Color("#c4ced9")); fontSize(15.px) } }) { Text("/ $soll") }
            } else if (editable) {
                Button({
                    if (saving) disabled()
                    attr("title", "Ist-Wert hier bearbeiten")
                    attr("aria-label", "Ist-Wert ${line.ist} von $soll für ${line.label} bearbeiten")
                    onClick { draft = line.ist.toString(); editing = true; confirming = false }
                    style { color(Color.white); property("background", "transparent"); border(0.px); padding(4.px); fontSize(15.px); cursor("pointer"); property("font-variant-numeric", "tabular-nums") }
                }) { Text("${line.ist} / $soll") }
            } else {
                Span({ style { color(Color("#c4ced9")); fontSize(15.px); property("font-variant-numeric", "tabular-nums") } }) { Text("${line.ist} / $soll") }
            }
        }
        if (line.erledigt && editable) {
            Button({
                if (saving) disabled()
                attr("title", "Aufgabe wieder öffnen")
                attr("aria-label", "${line.label} wieder öffnen")
                onClick { onSave(line.ist, false) }
                style { color(Color("#9ad7a7")); property("background", "transparent"); border(0.px); padding(4.px); fontSize(20.px); fontWeight("800"); cursor("pointer") }
            }) { Text("✓") }
        } else if (line.erledigt) {
            Span({ style { color(Color("#9ad7a7")); fontSize(20.px); fontWeight("800") } }) { Text("✓") }
        } else if (editable) {
            Button({
                if (saving) disabled()
                attr("title", if (confirming) "Ist-Wert übernehmen und Aufgabe erledigen" else "Aufgabe erledigen")
                attr("aria-label", if (confirming) "Ist-Wert übernehmen und ${line.label} erledigen" else "${line.label} erledigen")
                onClick {
                    if (line.soll == null || editing) commit(true)
                    else commit(true, line.soll)
                }
                style { color(Color.white); property("background", "transparent"); border(0.px); padding(4.px); fontSize(20.px); cursor("pointer") }
            }) { Text("✓") }
        }
    }
}

@Composable
private fun ViewerTaskHtml(taskId: String, html: String, running: Boolean) {
    var element by remember(taskId) { mutableStateOf<HTMLElement?>(null) }
    LaunchedEffect(html, element) {
        element?.let { if (it.innerHTML != html) it.innerHTML = html }
    }
    Div({
        ref { node ->
            element = node
            if (node.innerHTML != html) node.innerHTML = html
            onDispose { if (element == node) element = null }
        }
        style {
            color(if (running) Color("#ff747b") else Color("#c4ced9")); fontSize(13.px); lineHeight("1.45")
            property("white-space", "pre-wrap"); property("overflow-wrap", "anywhere")
            property("text-align", "center")
        }
    })
}

@Composable
private fun ViewerFocusContent(task: All?, nextTask: All?) {
    var frame by remember { mutableStateOf<HTMLIFrameElement?>(null) }
    val srcdoc = remember { decodedFocusHtml() }
    val taskContent = task?.let(::viewerTaskHtml).orEmpty()
    val nextTaskLabel = viewerNextTaskLabel(nextTask)
    val startAt = task?.let(::viewerTaskStart) ?: 0.0
    val endAt = task?.let(::viewerTaskEnd) ?: 0.0

    LaunchedEffect(task?.id, startAt, endAt, taskContent, nextTaskLabel, frame) {
            val message = js("({})")
            message.type = "lumatica-task-focus"
            message.taskId = task?.id ?: "idle"
            message.startAt = startAt
            message.endAt = endAt
            message.startTime = task?.uhrzeitVon.orEmpty()
            message.endTime = task?.uhrzeitBis.orEmpty()
            message.taskContent = taskContent
            message.empty = task == null
            message.nextTask = nextTaskLabel.orEmpty()
            repeat(10) {
                delay(100)
                frame?.contentWindow?.asDynamic()?.postMessage(message, "*")
            }
    }

    Div({ classes(AufgabenViewerStyles.focusPanel) }) {
            Iframe(attrs = {
                attr("title", "Fokus")
                attr("srcdoc", srcdoc)
                style { width(100.percent); height(100.percent); border(0.px); display(DisplayStyle.Block) }
                ref { element ->
                    frame = element
                    onDispose { if (frame == element) frame = null }
                }
            })
    }
}

